import React, {useState} from 'react';

import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {MultiPanePanel} from 'src/components/lib/multi-pane-panel';
import TemplateSelector from 'src/components/scheduler/template-selection/template-selector';
import {ComponentSelector} from './component-selector';

import {
  PanelBody,
  PanelHeader,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {SubTitleLarge} from '@spaced-out/ui-design-system/lib/components/Text';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {EditAddon} from './edit-addon';

import css from './scheduler-addon.css';


export const SCHEDULER_ADDON_ID = 'sense-scheduler-row';
export const SCHEDULER_LINK_TEXT = 'Schedule a meeting';

interface SchedulerAddonProps {
  onClose: () => void;
  onSubmit: (data: string, isEdit: boolean, type: string) => void;
  isSubmitting: boolean;
  editSchedulerInfo: {
    schedulerId: number | null;
    schedulerType: string | null;
  };
  standardEntityType: string;
}

export const SchedulerAddon = ({
  onClose,
  onSubmit,
  isSubmitting,
  editSchedulerInfo,
  standardEntityType,
}: SchedulerAddonProps) => {
  const {t, makeKey} = useLabelTranslation();

  const isEdit =
    editSchedulerInfo.schedulerType != null &&
    editSchedulerInfo.schedulerId != null;

  const [step, setStep] = useState<'selector' | 'component' | 'reselect'>(
    isEdit ? 'reselect' : 'selector',
  );
  const [templateId, setTemplateId] = useState<string | null>(null);

  const onTemplateSelect = (templateId: string) => {
    if (isEdit) {
      onSubmit(templateId, isEdit, editSchedulerInfo.schedulerType ?? '');
    } else {
      setTemplateId(templateId);
      setStep('component');
    }
  };

  const onComponentSubmit = (type: string) => {
    if (isEdit) {
      onSubmit(`${editSchedulerInfo.schedulerId}`, isEdit, type);
    } else {
      setTemplateId(templateId);
      setStep('component');
    }
    if (templateId) {
      onSubmit(templateId, false, type);
    }
  };

  const onEditSelect = (step: 'selector' | 'component') => {
    setStep(step);
  };

  const headerText =
    step === 'selector'
      ? 'Select a meeting template'
      : step === 'component'
      ? 'Select Display Type'
      : 'Scheduler Configuration';

  return (
    <MultiPanePanel
      onClose={onClose}
      isOpen={true}
      hideBackdrop={false}
      classNames={{backdrop: css.panelBackdrop}}
    >
      <>
        <PanelHeader onCloseButtonClick={onClose}>
          <>
            {step === 'component' && (
              <Button
                iconLeftName="chevron-left"
                onClick={() => setStep('selector')}
                type="ghost"
              />
            )}
            <SubTitleLarge>{t(makeKey(headerText), headerText)}</SubTitleLarge>
          </>
        </PanelHeader>
        <PanelBody>
          {isSubmitting ? (
            <CircularLoader className={css.loader} />
          ) : step === 'reselect' ? (
            <EditAddon
              onSelect={onEditSelect}
              schedulerId={
                editSchedulerInfo.schedulerId
                  ? `${editSchedulerInfo.schedulerId}`
                  : ''
              }
              schedulerType={editSchedulerInfo.schedulerType ?? ''}
            />
          ) : step === 'selector' ? (
            <TemplateSelector
              onSubmit={onTemplateSelect}
              standardEntityType={standardEntityType}
            />
          ) : step === 'component' ? (
            <ComponentSelector onSelect={onComponentSubmit} />
          ) : null}
        </PanelBody>
      </>
    </MultiPanePanel>
  );
};
